import {
  Color,
  Material,
  Mesh,
  MeshBasicMaterial,
  MeshPhysicalMaterial,
  MeshStandardMaterial,
  Object3D,
  PropertyBinding,
  Texture,
} from 'three';
import { resolvePaintFinishMaterial } from '../materials/paintFinish';
import { MESH_ROLES, type MeshSelector } from './meshRoles';
import type { InteriorOption, PaintOption, WheelOption } from '../../types';

/**
 * 매니페스트에 적힌 메시 이름을 **런타임에 실제로 붙는 이름**으로 바꿉니다.
 *
 * GLTFLoader는 노드 이름을 그대로 쓰지 않고 `PropertyBinding.sanitizeNodeName`을
 * 거칩니다. 애니메이션 트랙 경로에 쓸 수 있어야 해서 예약 문자(`. : / [ ]`)를 지우고
 * 공백을 밑줄로 바꿉니다. 그래서 에셋에 `PolySurface0.529_template_metalgraphite.002_0`로
 * 들어 있는 노드는 씬에서 `PolySurface0529_template_metalgraphite002_0`이 됩니다.
 *
 * 이걸 몰라서 실제로 고장났습니다. Sorento의 휠 색상과 M8의 휠·시트·트림 상당수가
 * 선택자에 점이 든 이름을 적어 두어 **한 번도 매칭되지 않았습니다**. 증상은 "옵션을
 * 눌러도 아무 일도 없다"였고, 이름이 에셋에 분명히 존재하기 때문에 오타로도 보이지
 * 않았습니다(assetIntegrity 검사도 원본 이름으로 대조해서 통과했습니다).
 *
 * 매니페스트를 고치지 않고 여기서 변환하는 이유: 매니페스트의 이름은 조사 도구
 * (`inspect-model.mjs`)가 찍어 주는 **에셋 그대로의 이름**이어야 합니다. 사람이 손으로
 * 점을 지워 적게 하면 도구 출력과 대조가 안 되고, 새 모델을 넣을 때마다 같은 함정을
 * 다시 밟습니다.
 *
 * three의 함수를 직접 부르는 이유는 규칙이 갈라지지 않게 하기 위해서입니다. 정규식을
 * 베껴 두면 three가 예약 문자를 늘릴 때 조용히 어긋납니다.
 */
function toRuntimeMeshName(name: string): string {
  return PropertyBinding.sanitizeNodeName(name);
}

/** 역할 -> 그 역할에 속한 메시들. 선택자에 없는 역할은 키 자체가 없습니다. */
export type RoleMeshMap = Record<string, Mesh[]>;

/** 역할 -> 그 역할에 할당할 머티리얼. */
export type RoleMaterialMap<T extends Material = Material> = Record<string, T>;

/**
 * @types/three의 Object3D에는 isMesh가 없어서 traverse 안에서 좁혀 줘야 합니다.
 * three는 instanceof 대신 이 플래그를 쓰도록 만들어져 있습니다(번들이 둘로 로드되는
 * 상황에서도 판정이 깨지지 않게).
 */
function isMesh(object: Object3D): object is Mesh {
  return (object as Mesh).isMesh === true;
}

/**
 * 메시에 붙은 머티리얼 이름들. 멀티 머티리얼 메시도 처리합니다.
 */
function materialNamesOf(mesh: Mesh): string[] {
  // three는 기본 머티리얼을 채워 주지만, 손으로 만든 메시에는 비어 있을 수 있습니다.
  const material: Material | Material[] | undefined | null = mesh.material;
  if (material === undefined || material === null) {
    return [];
  }
  const entries = Array.isArray(material) ? material : [material];
  return entries.map((entry) => entry?.name).filter((name): name is string => name !== undefined);
}

/**
 * 씬을 순회하며 역할별 메시를 수집합니다.
 *
 * 이름으로 단건 조회(getObjectByName)를 쓰지 않는 이유는 같은 이름의 메시가
 * 여러 개인 에셋이 흔해서 단건 조회로는 일부를 놓치기 때문입니다.
 *
 * 메시 이름은 toRuntimeMeshName을 거쳐서 대조합니다(그쪽 주석 참고). 머티리얼 이름은
 * 그대로 씁니다 — GLTFLoader가 머티리얼에는 `materialDef.name`을 손대지 않고 넣습니다.
 * 둘의 규칙이 다른 것이 이 함수에서 유일하게 헷갈리는 지점입니다.
 *
 * 선택 기준이 두 가지인 이유는 data/carModels.ts의 selectors 주석을 참고하세요.
 * 요약하면, 노드 이름이 자동 생성된 에셋에서는 머티리얼 이름이 유일한 단서인데
 * 머티리얼이 여러 부위에 공유되는 경우가 있어 그때는 메시 이름으로 좁혀야 합니다.
 *
 * 선택자에 없는 역할은 결과에서도 빠집니다. 모델마다 갖춘 역할이 달라서
 * (외관 전용 모델은 seat/trim이 없음) 없는 역할에 머티리얼이 할당되지 않아야 합니다.
 *
 * @returns 역할 -> 메시 배열 (선택자는 있지만 맞는 메시가 없으면 빈 배열)
 */
export function collectRoleMeshes(
  root: Object3D,
  selectors: Readonly<Record<string, MeshSelector | undefined>>
): RoleMeshMap {
  const meshNameToRole = new Map<string, string>();
  const materialNameToRole = new Map<string, string>();
  const roleMeshes: RoleMeshMap = {};

  Object.entries(selectors).forEach(([role, selector]) => {
    if (selector === undefined) {
      return;
    }
    (selector.meshes ?? []).forEach((name) => meshNameToRole.set(toRuntimeMeshName(name), role));
    (selector.materials ?? []).forEach((name) => materialNameToRole.set(name, role));
    roleMeshes[role] = [];
  });

  root.traverse((object) => {
    if (!isMesh(object)) {
      return;
    }

    // 메시 이름 지정이 머티리얼 지정보다 구체적이므로 우선합니다.
    let role = meshNameToRole.get(object.name);
    if (role === undefined) {
      role = materialNamesOf(object)
        .map((name) => materialNameToRole.get(name))
        .find((match) => match !== undefined);
    }
    if (role === undefined) {
      return;
    }
    roleMeshes[role].push(object);
  });

  return roleMeshes;
}

/**
 * 외장 페인트. 카클리어코트 표현이 필요하므로 MeshPhysicalMaterial을 씁니다.
 * 원본 GLB 머티리얼(MeshStandardMaterial)을 clone하면 clearcoat를 쓸 수 없어서 새로 만듭니다.
 */
export function createPaintMaterials(paint: PaintOption): RoleMaterialMap<MeshPhysicalMaterial> {
  const finish = resolvePaintFinishMaterial(paint.finish);
  return {
    [MESH_ROLES.PAINT]: new MeshPhysicalMaterial({
      color: new Color(paint.hex),
      metalness: finish.metalness,
      roughness: finish.roughness,
      clearcoat: finish.clearcoat,
      clearcoatRoughness: finish.clearcoatRoughness,
      envMapIntensity: 1.2,
    }),
  };
}

/**
 * 유리. 선택 옵션과 무관하므로 한 번만 만들어 재사용합니다.
 * 실내가 보여야 하므로 원본(alpha 0.5)보다 조금 더 투명하게 잡았습니다.
 */
export function createGlassMaterials(): RoleMaterialMap<MeshPhysicalMaterial> {
  return {
    [MESH_ROLES.GLASS]: new MeshPhysicalMaterial({
      color: new Color('#9fb4c8'),
      transparent: true,
      opacity: 0.3,
      roughness: 0.05,
      metalness: 0.1,
      envMapIntensity: 1.5,
      depthWrite: false,
    }),
  };
}

/**
 * 휠 림. 네 에셋 모두 스포크 형상이 baked라서 지오메트리는 바꿀 수 없고
 * 색상과 마감(metalness/roughness)만 옵션으로 제어합니다.
 */
export function createRimMaterials(wheel: WheelOption): RoleMaterialMap<MeshStandardMaterial> {
  return {
    [MESH_ROLES.RIM]: new MeshStandardMaterial({
      color: new Color(wheel.rimColor),
      metalness: wheel.rimMetalness,
      roughness: wheel.rimRoughness,
      envMapIntensity: 1.1,
    }),
  };
}

/**
 * 실내. 시트는 seatColor, 그 외 실내 표면(대시보드·도어트림·헤드라이너)은 trimColor.
 *
 * 모델마다 실내를 얼마나 잘게 나눌 수 있는지가 다릅니다. seat/trim 중 없는 역할은
 * collectRoleMeshes가 아예 반환하지 않으므로 여기서 만든 머티리얼도 쓰이지 않습니다.
 * (예: 718 Boxster는 실내가 통짜 메시 하나라 seat만 있습니다)
 */
export function createInteriorMaterials(interior: InteriorOption): RoleMaterialMap<MeshStandardMaterial> {
  return {
    [MESH_ROLES.SEAT]: new MeshStandardMaterial({
      color: new Color(interior.seatColor),
      roughness: 0.65,
      metalness: 0.05,
    }),
    [MESH_ROLES.TRIM]: new MeshStandardMaterial({
      color: new Color(interior.trimColor),
      roughness: 0.8,
      metalness: 0.05,
    }),
  };
}

/**
 * 수집한 메시에 역할별 머티리얼을 할당합니다.
 * 원본 머티리얼은 건드리지 않으므로 공유 머티리얼 오염이 발생하지 않습니다.
 */
export function applyRoleMaterials(
  roleMeshes: Readonly<Record<string, Mesh[] | undefined>>,
  roleMaterials: RoleMaterialMap
): void {
  Object.entries(roleMaterials).forEach(([role, material]) => {
    const meshes = roleMeshes[role];
    if (meshes === undefined) {
      return;
    }
    meshes.forEach((mesh) => {
      mesh.material = material;
    });
  });
}

/**
 * 조각 부조 엠블럼(스티어링 휠 크레스트 등).
 *
 * 왜 별도 역할인가:
 *   Taycan 에셋의 스티어링 허브(Object_20)는 삼각형 1,846개로 크레스트가 부조로
 *   새겨져 있는데, 원본 머티리얼이 metalness 0 / roughness 1로 완전 무광입니다.
 *   무광은 하이라이트를 만들지 않으므로 요철이 전혀 읽히지 않고 평평한 판처럼
 *   보입니다. 이 에셋에는 크레스트를 위한 별도 색이나 텍스처가 아예 없어서,
 *   형상을 드러내는 방법은 광택을 주는 것뿐입니다.
 *
 * 색을 실내 트림에서 가져오는 이유:
 *   밝은 금속으로 칠하면 크레스트뿐 아니라 에어백 커버 전체가 크롬 판이 됩니다.
 *   실제 스티어링 센터 패드는 어두운 재질이므로, 트림 색을 유지한 채 광택만
 *   올려서 피아노 블랙처럼 요철이 하이라이트로 읽히게 합니다.
 */
export function createEmblemMaterials(interior: InteriorOption): RoleMaterialMap<MeshStandardMaterial> {
  return {
    [MESH_ROLES.EMBLEM]: new MeshStandardMaterial({
      color: new Color(interior.trimColor),
      metalness: 0.45,
      roughness: 0.18,
      // 스튜디오 Lightformer 반사를 강하게 받아야 요철이 드러납니다.
      envMapIntensity: 1.8,
    }),
  };
}

/**
 * 실내 디스플레이. 쇼룸 로고를 띄웁니다.
 *
 * MeshBasicMaterial을 쓰는 이유는 화면이 조명을 받는 물체가 아니라 스스로 빛을
 * 내는 물체이기 때문입니다. MeshStandardMaterial을 쓰면 어두운 스튜디오에서
 * 화면이 꺼진 것처럼 검게 죽습니다.
 *
 * toneMapped: false는 톤 매핑을 건너뛰게 합니다. 이게 없으면 밝은 배경 프리셋에서
 * 화면이 주변과 같은 밝기로 뭉개져서 켜져 있다는 느낌이 사라집니다.
 *
 * @param displayTexture null이면 화면 역할을 건너뜁니다.
 */
export function createScreenMaterials(
  displayTexture: Texture | null | undefined
): RoleMaterialMap<MeshBasicMaterial> {
  if (displayTexture === null || displayTexture === undefined) {
    return {};
  }
  return {
    [MESH_ROLES.SCREEN]: new MeshBasicMaterial({ map: displayTexture, toneMapped: false }),
  };
}

/**
 * 번호판. 에셋이 들고 온 외국 번호판 대신 쇼룸 로고 판을 입힙니다.
 *
 * 화면(createScreenMaterials)과 달리 MeshStandardMaterial을 쓰는 이유:
 *   번호판은 스스로 빛나지 않고 조명을 받는 판입니다. 화면처럼
 *   MeshBasicMaterial + toneMapped:false로 만들면 어두운 스튜디오에서 번호판만
 *   발광해서, 차에 붙은 판이 아니라 공중에 뜬 표지판처럼 보입니다.
 *
 * 값의 근거:
 *   실물 번호판은 도색된 알루미늄이라 금속 반사가 거의 없고(metalness 0.05)
 *   반광입니다(roughness 0.45). envMapIntensity는 차체(1.2)보다 낮게 두어
 *   밝은 프리셋에서 판이 하얗게 타 로고가 사라지는 것을 막습니다.
 *
 * @param plateTexture null이면 번호판 역할을 건너뛰고 원본 판이 그대로 남습니다.
 */
export function createPlateMaterials(
  plateTexture: Texture | null | undefined
): RoleMaterialMap<MeshStandardMaterial> {
  if (plateTexture === null || plateTexture === undefined) {
    return {};
  }
  return {
    [MESH_ROLES.PLATE]: new MeshStandardMaterial({
      map: plateTexture,
      metalness: 0.05,
      roughness: 0.45,
      envMapIntensity: 0.9,
    }),
  };
}

export function disposeMaterials(roleMaterials: RoleMaterialMap): void {
  Object.values(roleMaterials).forEach((material) => material.dispose());
}

/** 우리가 만든 텍스처를 map으로 들고 있는 머티리얼. */
type MappedMaterial = Material & { map?: Texture | null };

/**
 * 우리가 캔버스로 만든 텍스처를 소유한 머티리얼을 정리합니다(화면·번호판).
 *
 * 별도 정리 함수가 필요한 이유:
 * three의 material.dispose()는 텍스처를 해제하지 않습니다. 이 머티리얼들의 map은
 * 에셋에서 온 것이 아니라 우리가 CanvasTexture로 만든 것이라, 여기서 함께
 * 해제하지 않으면 모델을 바꿀 때마다 GPU에 캔버스 텍스처가 쌓입니다.
 */
export function disposeTexturedMaterials(roleMaterials: RoleMaterialMap<MappedMaterial>): void {
  Object.values(roleMaterials).forEach((material) => {
    material.map?.dispose();
    material.dispose();
  });
}

// collectRoleMeshes를 재사용하려고 쓰는 내부 키. 역할이 아니라 선택자 문법만 빌립니다.
const HIDDEN_KEY = 'hidden';

/**
 * 에셋에 들어 있지만 보여선 안 되는 메시를 감춥니다.
 *
 * Sketchfab 에셋에는 변형용 오버레이 레이어가 섞여 오는 일이 흔합니다.
 * 예를 들어 Tesla Model 3 에셋의 `DAMAGE_GLASS`는 유리 파손 표현용 레이어인데
 * transparent=false / roughness=1 / normalMap 있는 **불투명** 머티리얼입니다.
 * 그대로 두면 윈드실드를 얼룩덜룩한 판으로 덮어서 운전석에서 앞이 안 보입니다.
 *
 * 머티리얼을 투명하게 갈아끼우는 대신 감추는 이유는, 이 레이어가 애초에
 * "온전한 차"에는 없어야 하는 것이기 때문입니다. 투명 유리를 한 겹 더 쌓으면
 * 유리 색만 진해지고 원인은 남습니다.
 */
export function hideMeshes(root: Object3D, selector: MeshSelector | undefined | null): void {
  if (selector === undefined || selector === null) {
    return;
  }
  collectRoleMeshes(root, { [HIDDEN_KEY]: selector })[HIDDEN_KEY].forEach((mesh) => {
    mesh.visible = false;
  });
}
